/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Page naming for the header `<h1>`, the browser tab and the route announcer.
 *
 * Accessibility survey `08-accessibility.md` #12: the heading came from an
 * exact-path lookup with no parameterised routes in it, so every detail page
 * (`/courses/:courseId`, `/groups/:groupId`, `/learn/courses/:courseId`, the
 * module and quiz views) rendered a literally empty `<h1>` — a screen reader
 * user pressing "jump to heading" landed on nothing. `document.title` was set
 * once in `index.html` and never again, so every tab read "AMIDA LMS".
 *
 * Two layers, in priority order:
 *
 * 1. What the page published for itself via {@link usePageTitle} — the real
 *    course, group or lesson name. This is what makes two open tabs tell
 *    themselves apart.
 * 2. {@link resolvePageTitle}, a route match that always yields a non-empty
 *    string. This is the floor: a page that publishes nothing (or has not
 *    loaded its name yet) still gets a heading, never `''`.
 */

import { useEffect, useId } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageTitleStore } from '@store/pageTitle.store';
import { APP_NAME } from './branding';

/** Suffix on `document.title`; matches the static title in `index.html`. */
export const DOCUMENT_TITLE_SUFFIX = 'AMIDA LMS';

/**
 * Last-resort heading. Reachable only for a path that is neither a static
 * entry nor a known pattern; the `*` route redirects to `/`, so in practice
 * this covers a route added without a title entry.
 */
export const FALLBACK_PAGE_TITLE = APP_NAME;

/** Exact-path titles. Every non-parameterised route in `App.tsx`. */
const STATIC_TITLES: Record<string, string> = {
  '/': APP_NAME,
  '/chat': 'Chat Studio',
  '/create-course': 'Create Course',
  '/dashboard': 'Dashboard',
  '/courses': 'Courses',
  '/users': 'Users & Groups',
  '/groups': 'Users & Groups',
  '/users-groups': 'Users & Groups',
  '/settings': 'Settings',
  '/doc': 'Documentation',
  '/api-docs': 'API Documentation',
  '/learn': 'My Courses',
  '/learn/dashboard': 'Dashboard',
  '/learn/certificates': 'Certificates',
  '/learn/badges': 'Badges',
  '/learn/profile': 'Profile',
  '/learn/ai-builder': 'AI Builder',
};

/**
 * Parameterised routes, longest-path-first so `/module/:id` is tested before
 * the bare course route it is nested under.
 */
const PATTERN_TITLES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/learn\/courses\/[^/]+\/module\/[^/]+\/?$/, 'Lesson'],
  [/^\/learn\/courses\/[^/]+\/quiz\/[^/]+\/?$/, 'Quiz'],
  [/^\/learn\/courses\/[^/]+\/?$/, 'Course'],
  [/^\/learn\/ai-builder\/[^/]+\/lesson\/[^/]+\/?$/, 'Lesson'],
  [/^\/learn\/ai-builder\/[^/]+\/?$/, 'Course'],
  [/^\/courses\/[^/]+\/?$/, 'Course'],
  [/^\/groups\/[^/]+\/?$/, 'Group'],
];

/**
 * A non-empty heading for any pathname. Trailing slashes are tolerated so
 * `/courses/abc/` names the same page as `/courses/abc`.
 */
export function resolvePageTitle(pathname: string): string {
  const exact = STATIC_TITLES[pathname] ?? STATIC_TITLES[pathname.replace(/\/+$/, '')];
  if (exact) return exact;
  for (const [pattern, title] of PATTERN_TITLES) {
    if (pattern.test(pathname)) return title;
  }
  return FALLBACK_PAGE_TITLE;
}

/** `"Course name · AMIDA LMS"` — what goes in the browser tab. */
export function documentTitleFor(pageTitle: string): string {
  const trimmed = pageTitle.trim();
  return trimmed ? `${trimmed} · ${DOCUMENT_TITLE_SUFFIX}` : DOCUMENT_TITLE_SUFFIX;
}

/**
 * Publishes this page's own name to the header `<h1>` and the browser tab.
 *
 * Call it with the real name once it has loaded and `null`/`undefined` before
 * then; the route-pattern floor covers the gap, so the heading is never blank.
 *
 * ```tsx
 * usePageTitle(course?.title, loading);
 * ```
 *
 * Pass `loading` — do not skip it. `/courses/A` -> `/courses/B` keeps the same
 * element mounted at the same `<Route>` position, and the detail hooks only
 * flip their loading flag; they never null the previous record. So mid-fetch
 * `course?.title` is still **A's** title, and publishing it makes the heading,
 * the browser tab and the route announcer confidently name the wrong record.
 * Falling back to the generic route floor is the honest answer, and the gate
 * lives here rather than as a ternary at each call site so it is one testable
 * rule instead of five copies.
 */
export function usePageTitle(title: string | null | undefined, loading?: boolean): void {
  const setTitle = usePageTitleStore((s) => s.setTitle);
  const clearTitle = usePageTitleStore((s) => s.clearTitle);
  // Stable for the life of this mount and unique across mounts, so the
  // outgoing page's cleanup cannot clear the incoming page's title even when
  // both published the same text.
  const owner = useId();
  const resolved = loading || !title?.trim() ? null : title.trim();

  useEffect(() => {
    if (!resolved) return;
    setTitle(owner, resolved);
    return () => clearTitle(owner);
  }, [owner, resolved, setTitle, clearTitle]);
}

/**
 * The heading for the current route: what the page published, else the
 * route-pattern floor. Never an empty string.
 */
export function useResolvedPageTitle(): string {
  const { pathname } = useLocation();
  const override = usePageTitleStore((s) => s.title);
  return override ?? resolvePageTitle(pathname);
}
